// Cuentas por pagar: deudas con proveedores, pagos (parciales o totales), vencimientos y origen del dinero.
// Lee las mismas cuentas que generan las compras a crédito de Caja. Un pago se registra como pago administrativo
// (una sola operación): desde la caja de la sucursal, por la propietaria o desde un banco (movimiento en Bancos).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { useSearchParams } from 'react-router-dom';
import { listCuentasPorPagar, getCuentaPorPagar, corregirCuentaPorPagar, anularCuentaPorPagar, anularPagoAdministrativo,
  crearPagoAdministrativo, listCuentasBancarias, subirComprobante } from '../../services/data';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import { toMoney } from '../../utils/numbers';
import { fechaCorta, hoyGT } from '../finanzas/estados';
import { AdjuntarComprobante, ComprobanteLink } from '../finanzas/Comprobante';
import PeriodoFiltro, { usePeriodo } from '../cierres/PeriodoFiltro';
import CierreDetalleModal from '../cierres/CierreDetalleModal';
import CompraCreditoModal from '../proveedores/CompraCreditoModal';
import MoneyInput from '../common/MoneyInput';
import '../finanzas/ui.css';
import '../proveedores/Proveedores.css';
import '../pagos-administrativos/PagosAdministrativos.css';
import FondoModal from '../common/FondoModal';

const ESTADOS = ['Pendiente', 'Parcialmente pagado', 'Pagado', 'Anulado'];
const VISTAS = [['', 'Todas'], ['con_saldo', 'Con saldo'], ['vencidas', 'Vencidas'], ['proximas', 'Próximas a vencer (7 días)'], ['pagadas', 'Pagadas']];
const claseEstado = { Pendiente: 'hoy', 'Parcialmente pagado': 'parcial', Pagado: 'completado', Anulado: 'proximo' };
const ORIGENES = [['Sucursal', 'Caja de la sucursal'], ['Propietaria', 'Propietaria (fuera de caja)'], ['Cuenta bancaria del negocio', 'Banco']];
const FORMAS = { Sucursal: ['Efectivo'], Propietaria: ['Efectivo', 'Transferencia', 'Tarjeta', 'Cheque'], 'Cuenta bancaria del negocio': ['Transferencia', 'Cheque'] };
const Estado = ({ c }) => <><span className={`fx-badge fx-badge--${claseEstado[c.estado]}`}>{c.estado}</span>
  {c.vencida && <span className="fx-badge fx-badge--vencido" style={{ marginLeft: 4 }}>Vencida</span>}</>;

/** Registrar pago: parcial o total, con el origen del dinero. No permite pagar más del saldo. */
function PagoModal({ cuenta: c, onClose, onSaved }) {
  const [f, setF] = useState({ monto: c.saldo.toFixed(2), fecha: hoyGT(), pagadoPor: 'Cuenta bancaria del negocio', formaPago: 'Transferencia', cuentaBancariaId: '', referencia: '', observacion: '' });
  const [archivo, setArchivo] = useState(null);
  const [cuentas, setCuentas] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { listCuentasBancarias(c.sucursalId).then(setCuentas).catch(() => setCuentas([])); }, [c.sucursalId]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const monto = Number(f.monto) || 0;
  const guardar = async (e) => {
    e.preventDefault();
    if (!(monto > 0)) return Swal.fire('Registrar pago', 'Indica el monto.', 'warning');
    if (monto > c.saldo + 0.001) return Swal.fire('Registrar pago', `El pago no puede superar el saldo (${toMoney(c.saldo)}).`, 'warning');
    if (f.pagadoPor === 'Cuenta bancaria del negocio' && !f.cuentaBancariaId) return Swal.fire('Registrar pago', 'Elige la cuenta bancaria.', 'warning');
    setBusy(true);
    try {
      const archivoUrl = archivo ? await subirComprobante(archivo, (archivo.name || 'comprobante').replace(/[^\w.-]+/g, '_'), archivo.type) : '';
      await crearPagoAdministrativo({ ...f, monto: monto.toFixed(2), sucursalId: c.sucursalId, cuentaPorPagarId: c.id, tipo: 'Pago a proveedor',
        beneficiario: c.proveedor, descripcion: c.referencia ? `Factura ${c.referencia}` : 'Pago a proveedor', archivoUrl });
      await onSaved();
    } catch (x) { Swal.fire('No se pudo registrar', x.message || '', 'error'); }
    finally { setBusy(false); }
  };
  return (
    <FondoModal onClose={onClose} style={{ zIndex: 1350 }}>
      <form className="fx-modal fx-modal--sm" role="dialog" aria-modal="true" aria-label="Registrar pago" onSubmit={guardar} onMouseDown={(e) => e.stopPropagation()}>
        <header><div><h2>Registrar pago</h2><p>{c.proveedor}{c.referencia ? ` · Fact. ${c.referencia}` : ''} · saldo <b>{toMoney(c.saldo)}</b></p></div>
          <button type="button" aria-label="Cerrar" onClick={onClose}>×</button></header>
        <div className="fx-form">
          <label>Monto<MoneyInput value={f.monto} onChange={(v) => setF({ ...f, monto: v })} required autoFocus /></label>
          <label>Fecha<input type="date" value={f.fecha} max={hoyGT()} onChange={set('fecha')} required /></label>
          <label>Origen del dinero<select value={f.pagadoPor} onChange={(e) => setF({ ...f, pagadoPor: e.target.value, formaPago: FORMAS[e.target.value][0], cuentaBancariaId: '' })}>
            {ORIGENES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
          <label>Forma de pago<select value={f.formaPago} onChange={set('formaPago')}>{FORMAS[f.pagadoPor].map((x) => <option key={x}>{x}</option>)}</select></label>
          {f.pagadoPor === 'Cuenta bancaria del negocio' && <label className="full">Cuenta bancaria<select value={f.cuentaBancariaId} onChange={set('cuentaBancariaId')} required>
            <option value="">Elige…</option>{cuentas.map((x) => <option key={x.id} value={x.id}>{x.banco} · {x.numero}{x.titular ? ` · ${x.titular}` : ''}</option>)}</select></label>}
          <label>Referencia<input value={f.referencia} maxLength={160} onChange={set('referencia')} /></label>
          <div className="pa-comprobante"><span>Comprobante</span><AdjuntarComprobante archivo={archivo} onChange={setArchivo} /></div>
          <label className="full">Observación<input value={f.observacion} maxLength={1000} onChange={set('observacion')} /></label>
        </div>
        <p className="fx-empty" style={{ padding: '6px 0 0' }}>{f.pagadoPor === 'Sucursal' ? 'Sale de la caja abierta de la sucursal (la fecha debe ser la del turno).'
          : f.pagadoPor === 'Propietaria' ? 'No afecta caja ni banco: solo reduce la deuda.' : 'Se registra la salida en Bancos, ligada a este pago.'}
          {' '}Nuevo saldo: <b>{toMoney(Math.max(0, c.saldo - monto))}</b>.</p>
        <div className="fx-actions"><button type="button" className="fx-btn" onClick={onClose}>Cancelar</button>
          <button className="fx-btn fx-btn--primary" disabled={busy}>{busy ? 'Guardando…' : 'Registrar pago'}</button></div>
      </form>
    </FondoModal>
  );
}

function DetalleModal({ id, onClose, onCambio }) {
  const [d, setD] = useState(null);
  const [pagando, setPagando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [cierre, setCierre] = useState(null);
  const cargar = useCallback(() => getCuentaPorPagar(id).then(setD).catch((e) => Swal.fire('Error', e.message || '', 'error')), [id]);
  useEffect(() => { cargar(); }, [cargar]);
  const listo = async () => { await cargar(); onCambio(); };
  const pedirMotivo = (titulo, texto) => Swal.fire({ title: titulo, text: texto, input: 'text', inputLabel: 'Motivo', showCancelButton: true,
    confirmButtonText: 'Anular', inputValidator: (v) => (!v?.trim() ? 'Indica el motivo' : undefined) }).then((r) => r.value?.trim());
  const anularPago = async (p) => {
    const motivo = await pedirMotivo('Anular pago', `${toMoney(p.monto)} · ${p.origen}. Se devuelve al saldo y se revierte su movimiento.`);
    if (!motivo) return;
    try { await anularPagoAdministrativo(p.id, motivo); await listo(); } catch (e) { Swal.fire('No se pudo anular', e.message || '', 'error'); }
  };
  const anularCuenta = async () => {
    const motivo = await pedirMotivo('Anular cuenta por pagar', 'Queda en el historial como anulada.');
    if (!motivo) return;
    try { await anularCuentaPorPagar(id, motivo); await listo(); } catch (e) { Swal.fire('No se pudo anular', e.message || '', 'error'); }
  };
  const guardarEdicion = async (e) => {
    e.preventDefault();
    try { await corregirCuentaPorPagar(id, editando); setEditando(null); await listo(); } catch (x) { Swal.fire('No se pudo guardar', x.message || '', 'error'); }
  };
  const c = d?.cuenta;
  return (
    <FondoModal onClose={onClose}>
      <section className="fx-modal" role="dialog" aria-modal="true" aria-label="Cuenta por pagar" onMouseDown={(e) => e.stopPropagation()}>
        <header><div><h2>{c ? c.proveedor : 'Cuenta por pagar'}</h2>{c && <p>{c.sucursal}{c.referencia ? ` · Fact. ${c.referencia}` : ''} · <Estado c={c} /></p>}</div>
          <button type="button" aria-label="Cerrar" onClick={onClose}>×</button></header>
        {!d ? <p className="fx-empty">Cargando…</p> : <>
          <section className="cd-resumen"><dl className="cd-resumen-grid cp-resumen">
            <div><dt>Total original</dt><dd>{toMoney(c.total)}</dd></div>
            <div><dt>Pagado</dt><dd>{toMoney(c.pagado)}</dd></div>
            <div><dt>Saldo pendiente</dt><dd style={{ color: c.saldo > 0 ? 'var(--warn-text)' : undefined }}>{toMoney(c.saldo)}</dd></div>
            <div><dt>Vencimiento</dt><dd style={{ color: c.vencida ? 'var(--danger)' : undefined }}>{c.vencimiento ? fechaCorta(c.vencimiento) : '—'}</dd></div>
          </dl></section>
          <h3 className="ces-h3">Datos de origen</h3>
          <dl className="cp-datos">
            <div><dt>Proveedor</dt><dd>{c.proveedor}{!c.proveedorActivo && ' (desactivado)'}</dd></div>
            <div><dt>Factura</dt><dd>{c.referencia || '—'}</dd></div>
            <div><dt>Fecha factura</dt><dd>{fechaCorta(c.fecha)}</dd></div>
            <div><dt>Sucursal</dt><dd>{c.sucursal}</dd></div>
            <div><dt>Registró</dt><dd>{c.compra?.cajero || c.registradaPor || '—'}</dd></div>
            <div><dt>Compra relacionada</dt><dd>{c.compra ? <>{c.compra.tipo} · {toMoney(c.compra.total)}{c.compra.cierreId && <> · <button type="button" className="fx-link" onClick={() => setCierre(c.compra.cierreId)}>Ver compra (en su cierre)</button></>}</> : 'Registro manual'}</dd></div>
            {c.observacion && <div><dt>Observación</dt><dd>{c.observacion}</dd></div>}
            {c.estado === 'Anulado' && <div><dt>Anulada</dt><dd>{c.anuladaMotivo}</dd></div>}
          </dl>
          <h3 className="ces-h3">Pagos realizados</h3>
          {!d.pagos.length ? <p className="fx-empty">Sin pagos registrados.</p> : <div className="ces-scroll"><table className="fx-table fx-table--stack">
            <thead><tr><th>Fecha</th><th className="num">Monto</th><th>Origen</th><th>Forma</th><th>Cuenta / caja</th><th>Usuario</th><th>Referencia</th><th>Comprobante</th><th /></tr></thead>
            <tbody>{d.pagos.map((p) => <tr key={`${p.tipo}-${p.id}`} style={p.estado === 'Anulado' ? { opacity: 0.55 } : undefined}>
              <td data-label="Fecha">{fechaCorta(p.fecha)}</td><td data-label="Monto" className="num"><b>{toMoney(p.monto)}</b></td>
              <td data-label="Origen">{p.origen}{p.estado === 'Anulado' && <small className="fx-empty" style={{ display: 'block', padding: 0 }}>Anulado: {p.anuladoMotivo}</small>}</td>
              <td data-label="Forma">{p.formaPago}</td><td data-label="Cuenta / caja">{p.cuenta}</td><td data-label="Usuario">{p.usuario}</td>
              <td data-label="Referencia">{p.referencia || '—'}</td><td data-label="Comprobante">{p.comprobante ? <ComprobanteLink url={p.comprobante} texto="Ver" /> : '—'}</td>
              <td>{p.anulable ? <button type="button" className="fx-link" style={{ color: 'var(--danger)' }} onClick={() => anularPago(p)}>Anular</button>
                : p.estado === 'Registrado' && p.anulaDesde ? <small className="fx-empty" title={`Se anula desde ${p.anulaDesde}`}>desde {p.anulaDesde}</small> : null}</td>
            </tr>)}</tbody>
          </table></div>}
          {editando && <form className="fx-form cp-editar" onSubmit={guardarEdicion}>
            <label>Vencimiento<input type="date" value={editando.vencimiento || ''} min={c.fecha} onChange={(e) => setEditando({ ...editando, vencimiento: e.target.value })} /></label>
            <label>Factura / referencia<input value={editando.referencia} maxLength={120} onChange={(e) => setEditando({ ...editando, referencia: e.target.value })} /></label>
            <label className="full">Observación<input value={editando.observacion} maxLength={1000} onChange={(e) => setEditando({ ...editando, observacion: e.target.value })} /></label>
            <div className="fx-actions full"><button type="button" className="fx-btn" onClick={() => setEditando(null)}>Cancelar</button><button className="fx-btn fx-btn--primary">Guardar</button></div>
          </form>}
          <div className="fx-actions">
            {c.estado !== 'Anulado' && !editando && <button type="button" className="fx-btn" onClick={() => setEditando({ vencimiento: c.vencimiento || '', referencia: c.referencia, observacion: c.observacion })}>Corregir datos</button>}
            {c.estado !== 'Anulado' && c.pagado <= 0 && !c.compra && <button type="button" className="fx-btn fx-btn--danger" onClick={anularCuenta}>Anular cuenta</button>}
            {c.saldo > 0 && <button type="button" className="fx-btn fx-btn--primary" onClick={() => setPagando(true)}>Registrar pago</button>}
          </div>
        </>}
        {pagando && <PagoModal cuenta={c} onClose={() => setPagando(false)} onSaved={async () => { setPagando(false); await listo(); }} />}
        {cierre && <CierreDetalleModal cierreId={cierre} onClose={() => setCierre(null)} />}
      </section>
    </FondoModal>
  );
}

export default function CuentasPorPagar() {
  const { sucursalId, todas } = useSucursalActiva();
  const per = usePeriodo('mes');
  const { desde, hasta } = per;
  const [filtros, setFiltros] = useState({ proveedorId: '', estado: '', vista: 'con_saldo' });
  const [buscar, setBuscar] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [nueva, setNueva] = useState(false);
  const [sp, setSp] = useSearchParams();
  const [detalle, setDetalle] = useState(null);
  useEffect(() => { const id = sp.get('ver'); if (id) { setDetalle(id); setSp({}, { replace: true }); } }, [sp, setSp]);
  useEffect(() => { const t = setTimeout(() => setQ(buscar.trim()), 300); return () => clearTimeout(t); }, [buscar]);
  const cargar = useCallback(() => {
    if (!sucursalId) return;
    listCuentasPorPagar({ desde, hasta, sucursalId, ...filtros, q }).then(setData).catch((e) => Swal.fire('Error', e.message || '', 'error'));
  }, [sucursalId, desde, hasta, filtros, q]);
  useEffect(() => { cargar(); }, [cargar]);
  const set = (k) => (e) => setFiltros((f) => ({ ...f, [k]: e.target.value }));
  const r = data?.resumen;
  const cuentas = useMemo(() => data?.cuentas || [], [data]);

  return (
    <div className="prov-shell">
      <header className="prov-header"><div><h1>Cuentas por pagar</h1>
        <p style={{ margin: 0, color: 'var(--ink-muted)' }}>Deudas con proveedores, pagos y vencimientos{todas ? ' · todas las sucursales' : ''}.</p></div>
        {!todas && <button className="fx-btn" onClick={() => setNueva(true)}>+ Registrar cuenta por pagar</button>}</header>

      {r && <section className="cd-resumen" aria-label="Resumen">
        <dl className="cd-resumen-grid cp-resumen">
          <div><dt>Total por pagar</dt><dd>{toMoney(r.totalPorPagar)}</dd></div>
          <div><dt>Vencido</dt><dd style={{ color: r.vencido > 0 ? 'var(--danger)' : undefined }}>{toMoney(r.vencido)}</dd></div>
          <div><dt>Próximos 7 días</dt><dd>{toMoney(r.proximos7)}</dd></div>
          <div><dt>Pagado en el período</dt><dd>{toMoney(r.pagadoPeriodo)}</dd></div>
        </dl>
      </section>}

      <section className="fx-card cc-filtros" aria-label="Filtros">
        <PeriodoFiltro p={per} />
        <div className="prov-filtros cc-grupo" role="group" aria-label="Filtros">
          <span className="filtro-titulo">Filtros</span>
          <input type="search" placeholder="Buscar proveedor, factura u observación" value={buscar} onChange={(e) => setBuscar(e.target.value)} aria-label="Buscar" />
          {data?.opciones.proveedores.length > 1 && <label>Proveedor<select value={filtros.proveedorId} onChange={set('proveedorId')}><option value="">Todos</option>
            {data.opciones.proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select></label>}
          <label>Estado<select value={filtros.estado} onChange={set('estado')}><option value="">Todos</option>{ESTADOS.map((x) => <option key={x}>{x}</option>)}</select></label>
          <label>Ver<select value={filtros.vista} onChange={set('vista')}>{VISTAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
        </div>
      </section>

      <div className="fx-card">
        {!data ? <p className="fx-empty">Cargando…</p> : cuentas.length ? (
          <table className="fx-table fx-table--stack">
            <thead><tr><th>Fecha</th><th>Proveedor</th><th>Sucursal</th><th>Factura</th><th className="num">Original</th><th className="num">Pagado</th><th className="num">Saldo</th><th>Vencimiento</th><th>Estado</th><th /></tr></thead>
            <tbody>{cuentas.map((c) => (
              <tr key={c.id}>
                <td data-label="Fecha">{fechaCorta(c.fecha)}</td>
                <td data-label="Proveedor"><b>{c.proveedor}</b>{!c.proveedorActivo && <small className="prov-sub">Desactivado</small>}</td>
                <td data-label="Sucursal">{c.sucursal}</td>
                <td data-label="Factura">{c.referencia || '—'}</td>
                <td data-label="Original" className="num">{toMoney(c.total)}</td>
                <td data-label="Pagado" className="num">{toMoney(c.pagado)}</td>
                <td data-label="Saldo" className="num"><b>{toMoney(c.saldo)}</b></td>
                <td data-label="Vencimiento" style={c.vencida ? { color: 'var(--danger)', fontWeight: 700 } : undefined}>{c.vencimiento ? fechaCorta(c.vencimiento) : '—'}</td>
                <td data-label="Estado"><Estado c={c} /></td>
                <td><button className="fx-btn" onClick={() => setDetalle(c.id)}>Ver detalle</button></td>
              </tr>))}</tbody>
          </table>
        ) : <p className="fx-empty">No hay cuentas con estos filtros.</p>}
      </div>
      {detalle && <DetalleModal id={detalle} onClose={() => setDetalle(null)} onCambio={cargar} />}
      {nueva && <CompraCreditoModal sucursalId={sucursalId} onClose={() => setNueva(false)} onSaved={() => { setNueva(false); cargar(); }} />}
    </div>
  );
}
